import { Box } from '@mui/material'
import Grid from '@mui/material/Grid2'
import { Add } from '@mui/icons-material'
import { Table, useSampleImporter } from 'components'
import { Button } from 'components/ui'
import { CrudAction, Entity, usePermissions } from 'utils/hooks'
import { timeFilters, TFlourTimeFilterValues } from 'types/models/Flours'
import { analysisListColumns } from './columns'
import { CardWithTitle } from './components/CardWithTitle'
import { SelectDates } from './components/SelectDates'
import { DeliveryListDetails } from './DeliveryListDetails'
import * as sxStyles from './AnalyticsDetails.styled'
import React, { ChangeEvent, Dispatch, SetStateAction } from 'react'
import { useNirService } from '../../../../services'
import { AnalyticsListMode } from '../list/Analytics'
import { useQuery, useQueryClient } from 'react-query'
import { useAnalyticsServices } from '../services/services'

interface IFloursListProps {
  mode: AnalyticsListMode
  deliveriesDates: any[]
  isLoading?: boolean
  timeFilter: TFlourTimeFilterValues
  onTimeFilterChange: (val: TFlourTimeFilterValues) => void
  productId?: string
  selectedDeliveryId?: string
  setSelectedDeliveryId?: Dispatch<SetStateAction<string | undefined>>
}

export const AnalyticsList: React.FC<IFloursListProps> = ({
  mode,
  deliveriesDates,
  isLoading,
  timeFilter,
  onTimeFilterChange,
  productId,
  selectedDeliveryId,
  setSelectedDeliveryId,
}) => {
  const { checkPermission } = usePermissions()
  const queryClient = useQueryClient()
  const isBakerModule = mode === 'products'

  const { getFlourProductById } = useAnalyticsServices(isBakerModule)

  const prefix = isBakerModule ? 'baker' : 'flour'
  const { uploadFileAndStream } = useNirService(prefix)
  const { handleUpload, fileInputRef } = useSampleImporter({
    uploadFileAndStream,
    onSuccessCallback: () => {
      void queryClient.invalidateQueries('arrival-dates-by-period')
    },
    prefix,
  })

  const { data: flourProductSpec } = useQuery({
    queryKey: ['analytics-product-spec-by-id', productId],
    queryFn: () => getFlourProductById(productId),
  })

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    await handleUpload(event, {
      product_id: productId,
      post_aging: mode === 'post-aging',
    })
  }

  const handleProcessAnalysis = async () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const isUploading = !!localStorage.getItem(`${prefix}_nir_job_id`)

  return (
    <Grid container spacing={1} sx={sxStyles.flourListGrid}>
      <Grid size={12}>
        <CardWithTitle title={flourProductSpec?.result?.name || 'Flour'} />
      </Grid>

      <Grid size={12} sx={sxStyles.flourListUpperGrid}>
        <Box sx={sxStyles.FlourListActionBox}>
          <SelectDates
            selectLabel=""
            selectList={timeFilters}
            selected={timeFilter}
            handleSelectChange={onTimeFilterChange}
          />
          {checkPermission(
            isBakerModule ? Entity.BAKER_ANALYTICS : Entity.FLOURS,
            CrudAction.CREATE
          ) && (
            <Button
              startIcon={<Add />}
              onClick={handleProcessAnalysis}
              disabled={isUploading}
            >
              New Analysis
            </Button>
          )}
        </Box>

        <Box sx={sxStyles.flourListTableBox}>
          <Table
            showHeader={false}
            showPagination={false}
            columns={analysisListColumns()}
            data={deliveriesDates}
            isLoading={isLoading}
            renderExpandableRow={(row) => (
              <Box sx={sxStyles.flourDeliveryListBox}>
                <DeliveryListDetails
                  listKey={row.id}
                  date={row.date}
                  productId={productId!}
                  selectedDeliveryId={selectedDeliveryId}
                  setSelectedDeliveryId={setSelectedDeliveryId}
                  mode={mode}
                />
              </Box>
            )}
            showExpandableRowAtRight
          />
        </Box>
      </Grid>

      <input
        hidden
        type="file"
        ref={fileInputRef}
        accept=".dx,.csv"
        onChange={handleFileChange}
      />
    </Grid>
  )
}
